import { Button, Divider, Skeleton, Stack, Typography } from '@mui/material'
import numeral from 'numeral'
import { Fragment, useMemo, useState } from 'react'
import { Icons, InformationModal, SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  ResaleItemBoxFragment,
  UserBankAccountFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  ResaleItem as OResaleItem,
  ResaleItemStatus,
  ResaleTag,
} from '@/components/organisms'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { encodeSkuId, getItemSkuPrice } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  submitting: boolean
  resaleItem: Maybe<ResaleItemBoxFragment> | undefined
  user: Maybe<UserBankAccountFragment> | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onCancelListing: () => Promise<void>
}

export const ResaleDetailTemplate: Template<State, Action> = ({
  state,
  onCancelListing,
}) => {
  const { t } = useTranslation('resaleDetail')
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const { routerPages, generatePath } = useRouter()

  return (
    <BaseLayout
      margin
      customNavigation={
        state.resaleItem ? (
          <FixedBottomCell
            submitting={state.submitting}
            handleOpenModal={() => setModalOpen(true)}
            resaleItem={state.resaleItem}
          />
        ) : null
      }
      header={{
        back: true,
        defaultBackPage: generatePath(routerPages.ResaleManagement),
        title: t('title'),
      }}
    >
      {state.loading || !state.resaleItem ? (
        <>
          <StatusSkeleton />
          <DetailSkeleton />
        </>
      ) : (
        <>
          <StatusCell status={state.resaleItem.status} />
          <DetailCell resaleItem={state.resaleItem} />
        </>
      )}

      <InformationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('confirmCancelListingTitle')}
        content={t('confirmCancelListingDesc')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            onClick={() => {
              onCancelListing().then(() => setModalOpen(false))
            }}
            disabled={!state.resaleItem}
            loading={state.submitting}
          >
            {t('confirm')}
          </Button>
        }
        cancelText={t('close')}
      />
    </BaseLayout>
  )
}

type StatusCellProps = {
  status: string
}
const StatusCell: Cell<StatusCellProps> = ({ status }) => {
  const { t } = useTranslation('resaleDetail')
  const { onClick } = useBrowserLink(getHelpUrl('appHelp'))
  return (
    <CellFrame sx={{ py: 7 }}>
      <Stack
        direction="row"
        justifyContent="center"
        spacing={3}
        alignItems="center"
      >
        <Icons.Check sx={{ color: 'primary.main' }} />
        <Typography variant="h6">{t(`${status}Status`)}</Typography>
      </Stack>
      {status !== ResaleItemStatus.Matched ? (
        <Stack sx={{ pt: 2 }}>
          <Typography variant="subtitle1" color="text.secondary">
            {t(`${status}StatusDesc`)}
          </Typography>
          {status === ResaleItemStatus.Sold && (
            <Stack sx={{ pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('soldCaption1')}
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{ cursor: 'pointer' }}
                  onClick={onClick}
                >
                  {t('soldCaptionLink')}
                </Typography>
                {t('soldCaption2')}
              </Typography>
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack sx={{ pt: 2 }}>
          <Stack
            sx={{
              mt: 7,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'primary.main',
            }}
          >
            <Typography
              variant="body2"
              color="primary.main"
              sx={{ py: 2, px: 4 }}
            >
              {t('notCancelListingDesc')}
            </Typography>
          </Stack>
        </Stack>
      )}
    </CellFrame>
  )
}

export const StatusSkeleton = () => (
  <CellFrame sx={{ pt: 7 }}>
    <Stack alignItems="center">
      <SkeletonText variant="h6" width="60%" />
    </Stack>
  </CellFrame>
)

type DetailCellProps = {
  resaleItem: ResaleItemBoxFragment
}
const DetailCell: Cell<DetailCellProps> = ({ resaleItem }) => {
  const { t } = useTranslation('resaleDetail')
  const { price, formattedPrice } = useMemo(
    () => getItemSkuPrice(resaleItem.item),
    [resaleItem.item]
  )

  const data = [
    {
      label: t('ticketPrice', '価格'),
      value: `￥${numeral(resaleItem.totalItemPrice).format('0,0')}`,
    },
    {
      // リセール手数料 = チケット代金 * 代金に対する割合 * 枚数
      label: t('resaleFee', '販売手数料'),
      value: `-¥${numeral(
        price *
          (resaleItem.item?.metadata?.ticket?.resaleFee?.feeRatePerTicket ||
            0) *
          resaleItem.itemCount
      ).format('0,0')}`,
    },
    {
      // 振り込み手数料 = 1件あたりの振り込み手数料
      label: t('transferFee', '振込手数料'),
      value: `-¥${numeral(
        resaleItem.item?.metadata?.ticket?.resaleFee?.bankTransferFeePerOrder ||
          0
      ).format('0,0')}`,
    },
  ]
  const totalAmount = useMemo(() => {
    return numeral(
      data.reduce((acc, curr) => {
        const value = curr.value
          ? parseInt(
              curr.value.replace('￥', '').replace('¥', '').replace(/,/g, ''),
              10
            )
          : 0
        return acc + value
      }, 0)
    ).format('0,0')
  }, [
    resaleItem.item?.metadata?.ticket?.resaleFee?.bankTransferFeePerOrder,
    resaleItem.item.price,
    resaleItem.itemCount,
  ])

  return (
    <CellFrame>
      <OResaleItem title={t('ticketInfo', 'チケット情報')}>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography variant="h5">{resaleItem.item.title}</Typography>
            {!!resaleItem?.item?.sku?.unitName && (
              <Typography variant="subtitle1">
                {resaleItem?.item?.sku?.unitName}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <ResaleTag status={resaleItem.status} />
            {resaleItem.status === ResaleItemStatus.Paid && (
              <Typography color="primary.main" variant="subtitle2">
                {t('paid', '振込完了')}
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" sx={{ pt: 3 }}>
            {resaleItem.item.metadata?.ticket?.description}
          </Typography>
        </Stack>
      </OResaleItem>
      <OResaleItem title={t('exhibitionInfo', '出品情報')}>
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {t('ticketPrice', '価格')}
            </Typography>
            <Typography variant="body2">
              ¥{formattedPrice}/1{t('perSheet', '枚')}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {t('holdingQuantity', '出品枚数')}
            </Typography>
            <Typography variant="body2">
              {resaleItem.itemCount}
              {t('sheet')}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {t('exhibitionDate', '出品日時')}
            </Typography>
            <Typography variant="body2">
              {resaleItem.formattedListedAt}
            </Typography>
          </Stack>
        </Stack>
      </OResaleItem>

      {[
        ResaleItemStatus.Matched,
        ResaleItemStatus.Open,
        ResaleItemStatus.Paid,
        ResaleItemStatus.Sold,
      ].includes(resaleItem.status as ResaleItemStatus) && (
        <OResaleItem title={t('paymentResalePrice', 'リセール代金のお支払い')}>
          <Stack sx={{ pb: 3 }}>
            {data.map(({ value, label }, index) => (
              <Fragment key={index}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ py: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Stack>
                <Divider />
              </Fragment>
            ))}
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ py: 2 }}
            >
              <Typography variant="h6">{t('total', '合計')}</Typography>
              <Typography variant="h6">¥{totalAmount}</Typography>
            </Stack>
          </Stack>
        </OResaleItem>
      )}
    </CellFrame>
  )
}

export const DetailSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 6 }}>
    <Stack sx={{ pt: 9 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <SkeletonText variant="body2" numberOfLine={2} lastWidth="25%" />
    </Stack>
    <Stack sx={{ pt: 6 }}>
      <SkeletonText variant="body2" numberOfLine={5} />
    </Stack>
    <Stack sx={{ pt: 9 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <SkeletonText variant="body2" numberOfLine={6} />
    </Stack>
    <Stack sx={{ pt: 9 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <SkeletonText variant="body2" numberOfLine={6} />
    </Stack>
  </CellFrame>
)

type FixedBottomCellProps = {
  submitting: boolean
  resaleItem?: ResaleItemBoxFragment
  handleOpenModal: () => void
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  submitting,
  resaleItem,
  handleOpenModal,
}) => {
  const status = resaleItem?.status
  const { t } = useTranslation('resaleDetail')
  const { routerPages, router, generatePath } = useRouter()
  const { btnText, variant, disabled, onClick } = useMemo<{
    btnText: string
    variant: 'contained' | 'outlined'
    disabled: boolean
    onClick?: () => void
  }>(() => {
    switch (status) {
      case ResaleItemStatus.Open:
      case ResaleItemStatus.Matched:
        return {
          btnText: t('cancelListing'),
          variant: 'outlined',
          disabled: status === ResaleItemStatus.Matched,
          onClick() {
            handleOpenModal()
          },
        }
      case ResaleItemStatus.Canceled:
      case ResaleItemStatus.Sold:
      case ResaleItemStatus.NoMatched:
      default:
        return {
          btnText: t('viewTicketDetail'),
          variant: 'contained',
          disabled: false,
          onClick() {
            router.push({
              pathname: generatePath(routerPages.OwnedItemDetail, {
                itemId: resaleItem?.item?.id,
              }),
              search: `${
                resaleItem?.skuId
                  ? `?skuId=${encodeSkuId(resaleItem.skuId)}`
                  : ''
              }`,
            })
          },
        }
    }
  }, [status, t, submitting])

  return (
    <Stack alignItems="center" sx={{ py: 4, bgcolor: 'grey.50' }}>
      <Button
        variant={variant}
        disabled={disabled}
        size="large"
        sx={{ width: 244 }}
        onClick={onClick}
      >
        {btnText}
      </Button>
    </Stack>
  )
}
